import React, { FC, useContext, useEffect, useMemo } from 'react';
import { useFormikContext } from 'formik';

import { Row, Column, FormInput } from 'Shared/components';
import { formatValueWithCommas, stringToBoolean } from 'Shared/utils';
import { env } from 'Shared/constants/env';
import {
  editVehicleOwnershipPolicy,
  editVehicleRidesharePolicy,
  editVehicleUseTypePolicy,
  editVehicleMileagePolicy,
  editVehicleLienholderPolicy,
} from 'Admin/authPolicies';
import { PolicyContext } from 'Policies/utils';
import { AddVehicleFormValues } from '.';
import styles from './styles.module.css';
import { VehicleOptionsResponse } from '@clearcover/core-app-types';
import { VehicleUsage } from './VehicleUsage';
import { VehicleBusinessLabel } from 'QuoteApp/components/VehiclesList/VehicleForm/BusinessUseForm/VehicleBusinessLabel';

type VehicleDetailsProps = {
  data: VehicleOptionsResponse;
  rideshareOffered: boolean;
};

export const VehicleDetails: FC<VehicleDetailsProps> = ({
  data,
  rideshareOffered,
}) => {
  const { resourceEffectiveGrants } = useContext(PolicyContext);

  const {
    handleBlur,
    setFieldValue,
    setValues,
    handleChange,
    values,
    touched,
    errors,
    initialValues,
  } = useFormikContext<AddVehicleFormValues>();

  const {
    askForAntiTheftDevice,
    askForBusinessUseType,
    askForAvgMilesPerYear,
    askForRegisteredToPolicyholderOrSpouse,
  } = data.vehicleOptionsQuestionsConfig;

  useEffect(() => {
    if (askForBusinessUseType && values.primaryUse === 'pleasure') {
      setFieldValue('unacceptableBusinessUse', null);
    } else if (askForBusinessUseType && values.primaryUse === 'business') {
      setFieldValue(
        'unacceptableBusinessUse',
        initialValues.unacceptableBusinessUse
      );
    }
  }, [askForBusinessUseType, values.primaryUse, setFieldValue, initialValues]);

  const businessUseOptions = useMemo(
    () =>
      data.businessUseTypes?.reduce(
        (
          acc: Record<string, string>,
          { label, value }: { label: string; value: string }
        ) => {
          return {
            ...acc,
            [value]: label,
          };
        },
        {}
      ),
    [data.businessUseTypes]
  );

  // check that this makes sense
  const canEditUseType =
    values.type === 'create' ||
    editVehicleUseTypePolicy(resourceEffectiveGrants);

  const canEditRideshare =
    values.type === 'create' ||
    editVehicleRidesharePolicy(resourceEffectiveGrants);

  const canEditOwnership =
    values.type === 'create' ||
    editVehicleOwnershipPolicy(resourceEffectiveGrants);

  const canEditLienholder =
    values.type === 'create' ||
    editVehicleLienholderPolicy(resourceEffectiveGrants);

  const canEditMileage =
    values.type === 'create' ||
    editVehicleMileagePolicy(resourceEffectiveGrants);

  const vehicleUsageOptions = data.vehicleUsageTypes.reduce(
    (acc: {}, { label, identifier }) => {
      return {
        ...acc,
        [identifier]: label,
      };
    },
    {}
  );

  return (
    <div className={styles.section} data-testid="vehicle-modal-details">
      <h1>Details</h1>

      <Row>
        {askForAntiTheftDevice && (
          <Column>
            <FormInput
              variant="radioButtonGroup"
              name="hasAntiTheftDevice"
              options={{ true: 'yes', false: 'no' }}
              value={values.hasAntiTheftDevice?.toString() || ''}
              onChange={(value: string) =>
                setFieldValue('hasAntiTheftDevice', stringToBoolean(value))
              }
              label="Vehicle equipped with anti-theft device"
              data-testid="equipped-with-anti-theft-device"
            />
          </Column>
        )}
        {askForBusinessUseType && (
          <>
            <Column>
              <VehicleBusinessLabel label="Is used for any business purposes?" />
              <FormInput
                variant="radioButtonGroup"
                name="primaryUse"
                options={{ business: 'yes', pleasure: 'no' }}
                value={values.primaryUse}
                onChange={(value: string) => {
                  setFieldValue('primaryUse', value);
                }}
                label=""
                data-testid="primary-use"
              />
            </Column>
            <Column>
              <VehicleBusinessLabel label="Business use type" />
              <FormInput
                variant="selectAutocomplete"
                name="unacceptableBusinessUse"
                data-testid="unacceptable-business-use"
                autoComplete={'off'}
                onChange={(value: string) => {
                  setFieldValue(
                    'unacceptableBusinessUse',
                    value === 'unacceptable'
                  );
                }}
                onBlur={handleBlur}
                value={
                  values.unacceptableBusinessUse === true
                    ? 'unacceptable'
                    : values.unacceptableBusinessUse === false
                    ? 'acceptable'
                    : ''
                }
                label=""
                disabled={values.primaryUse !== 'business'}
                options={businessUseOptions ?? {}}
              />
            </Column>
          </>
        )}
        {askForAvgMilesPerYear && (
          <Column>
            <FormInput
              variant="text"
              name="avgMiles"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                canEditMileage &&
                setFieldValue(
                  'avgMiles',
                  formatValueWithCommas(e.currentTarget.value)
                )
              }
              onBlur={handleBlur}
              value={values.avgMiles}
              errorMessage={touched.avgMiles ? errors.avgMiles : undefined}
              label="Avg Miles Driven Per Year"
              data-testid="avg-miles"
              disabled={!canEditMileage}
            />
          </Column>
        )}
        <Column hidden={!rideshareOffered}>
          <FormInput
            variant="radioButtonGroup"
            name="rideshare"
            options={{ yes: 'yes', no: 'no' }}
            value={values.rideShare}
            onChange={(value: string) =>
              canEditRideshare && setFieldValue('rideShare', value)
            }
            label="Rideshare"
            data-testid="ride-share"
            disabled={!canEditRideshare}
          />
        </Column>
        {!askForBusinessUseType && (
          <Column>
            <FormInput
              name="primaryUse"
              value={values.primaryUse}
              onChange={(value: string) =>
                canEditUseType && setFieldValue('primaryUse', value)
              }
              variant="radioButtonGroup"
              options={vehicleUsageOptions}
              label="Primary Use"
              data-testid="primary-use"
              disabled={!canEditUseType}
            />
          </Column>
        )}
      </Row>

      <Row>
        {askForRegisteredToPolicyholderOrSpouse && (
          <Column variant="threeFourths">
            <FormInput
              variant="radioButtonGroup"
              name="registeredToPolicyholderOrSpouse"
              options={{ true: 'yes', false: 'no' }}
              value={values.registeredToPolicyholderOrSpouse?.toString() || ''}
              onChange={(value: string) =>
                setFieldValue(
                  'registeredToPolicyholderOrSpouse',
                  stringToBoolean(value)
                )
              }
              label="Is applicant or spouse the registered owner or lessee?"
              data-testid="registered-to-policyholder-or-spouse"
            />
          </Column>
        )}
        <Column variant="threeFourths">
          <FormInput
            name="ownershipStatus"
            value={values.ownershipStatus}
            onChange={(value: string) => {
              if (canEditOwnership) {
                if (value === 'owned') {
                  setValues({
                    ...values,
                    ownershipStatus: value,
                    lienholder: {
                      name: '',
                      address1: '',
                      address2: '',
                      city: '',
                      state: '',
                      zip: '',
                    },
                  });
                } else {
                  setFieldValue('ownershipStatus', value);
                }
              }
            }}
            variant="radioButtonGroup"
            options={{
              financed: 'financed',
              leased: 'leased',
              owned: 'owned',
            }}
            disabled={!canEditOwnership}
            label="Ownership Status"
            data-testid="ownership-status"
          />
        </Column>
        <Column variant="threeFourths">
          <FormInput
            variant="text"
            name="lienholder.name"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.lienholder?.name}
            disabled={values.ownershipStatus === 'owned' || !canEditLienholder}
            errorMessage={
              touched.lienholder?.name ? errors.lienholder?.name : undefined
            }
            label="Lienholder Name"
            data-testid="leinholder-name"
          />
        </Column>
      </Row>
      <FormInput
        variant="addressAutocomplete"
        prefix="lienholder"
        values={{
          address1: values.lienholder?.address1,
          address2: values.lienholder?.address2,
          city: values.lienholder?.city,
          state: values.lienholder?.state,
          zip: values.lienholder?.zip,
        }}
        onAddressSelect={selectedOption => {
          const { city, streetLine, state } = selectedOption;
          setValues({
            ...values,
            lienholder: {
              ...values.lienholder,
              address1: streetLine,
              city: city,
              state: state,
              zip: '',
            },
          });
        }}
        onAddressLine1Change={value =>
          setFieldValue('lienholder.address1', value)
        }
        onAddressLine2Change={value =>
          setFieldValue('lienholder.address2', value)
        }
        onCityChange={value => setFieldValue('lienholder.city', value)}
        onStateChange={value => setFieldValue('lienholder.state', value)}
        onZipChange={value => setFieldValue('lienholder.zip', value)}
        errorMessages={{
          address1: touched.lienholder?.address1
            ? errors.lienholder?.address1
            : undefined,
          address2: touched.lienholder?.address2
            ? errors.lienholder?.address2
            : undefined,
          city: touched.lienholder?.city ? errors.lienholder?.city : undefined,
          state: touched.lienholder?.state
            ? errors.lienholder?.state
            : undefined,
          zip: touched.lienholder?.zip ? errors.lienholder?.zip : undefined,
        }}
        onBlur={handleBlur}
        disabled={values.ownershipStatus === 'owned' || !canEditLienholder}
      />
      {data.vehicleOptionsQuestionsConfig.askForUsageDetails &&
        env.vehicleNewQuestionsEnabled && <VehicleUsage />}
    </div>
  );
};
